import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Building2, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { SiteOverviewBar } from "@/components/chef/etablissements/SiteOverviewBar";
import { SiteCard } from "@/components/chef/etablissements/SiteCard";
import { SiteDetailSheet } from "@/components/chef/etablissements/SiteDetailSheet";
import { SiteInfoDialog } from "@/components/chef/etablissements/SiteInfoDialog";
import { monthKey, monthLabel, shiftMonth, fullName } from "@/components/etablissement/shared";

export default function ChefSites() {
  const [month, setMonth] = useState(monthKey());
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [openSite, setOpenSite] = useState(null);
  const [dialog, setDialog] = useState({ open: false, site: null });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chef/etablissements/overview", { params: { month } });
      setOv(data);
      setNotEnabled(false);
    } catch (e) {
      if (e.response?.status === 403) setNotEnabled(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSite = async (form) => {
    const body = { ...form, name: form.name.trim() };
    await run(async () => {
      if (dialog.site) await api.patch(`/chef/sites/${dialog.site.id}`, body);
      else await api.post("/chef/sites", body);
    }, dialog.site ? "Établissement mis à jour" : "Établissement créé");
    setDialog({ open: false, site: null });
  };

  if (notEnabled) {
    return (
      <div className="p-8 max-w-3xl" data-testid="sites-not-enabled">
        <div className="border border-[#DEE2E6] p-8 bg-[#F8F9FA]">
          <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-3">// Module verrouillé</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-4">Multi-établissements non activé</h1>
          <p className="text-[#495057] mb-6">Gérez plusieurs établissements (agences, franchises, campings…) sous une même entreprise : salariés, responsables, planning, finance et statistiques par site, avec une vue globale au siège. Cette fonctionnalité doit être activée par un administrateur PAG.</p>
          <a href="mailto:contact@parcavenue.eu?subject=Activation module Multi-établissements" className="inline-flex items-center h-11 px-5 bg-[#0A0A0A] text-white font-semibold text-sm hover:bg-[#002FA7] transition-colors">Demander l&apos;activation à PAG</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="chef-sites-page">
      <PageHeader eyebrow="Établissements" title="Vue siège"
        sub="Pilotez tous vos établissements : équipes, responsables, planning, finance et saisons. Chaque responsable ne voit que son site."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="ov-month-prev" className="h-10 px-2"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="h-10 px-4 border border-[#DEE2E6] bg-white flex items-center text-sm font-semibold min-w-[170px] justify-center" data-testid="ov-month-label">{monthLabel(month)}</div>
            <Button variant="outline" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="ov-month-next" className="h-10 px-2"><ChevronRight className="h-4 w-4" /></Button>
            <Button onClick={() => setDialog({ open: true, site: null })} data-testid="add-site-btn" className="h-10 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un établissement
            </Button>
          </div>
        } />

      <div className="p-6 lg:p-10 space-y-8">
        {loading || !ov ? <div className="text-[#868E96]">Chargement…</div> : (
          <>
            <SiteOverviewBar totals={ov.totals} entreprise={ov.entreprise}
              onMode={(m) => run(() => api.put("/chef/etablissements/settings", { network_mode: m }), m === "franchise" ? "Mode franchise activé" : "Mode réseau intégré activé")}
              onSeasonal={(v) => run(() => api.put("/chef/etablissements/settings", { seasonal_mode: v }), v ? "Mode saisonnier activé" : "Mode saisonnier désactivé")} />

            <div>
              <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#868E96] font-bold">// Réseau</div>
                  <h2 className="font-display text-2xl font-black tracking-tight">Mes établissements</h2>
                </div>
                {ov.unassigned.length > 0 && (
                  <div className="flex items-center gap-2 text-xs bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] px-3 py-2" data-testid="ov-unassigned">
                    <Users className="h-3.5 w-3.5" />
                    <span className="font-semibold">{ov.unassigned.length} salarié{ov.unassigned.length > 1 ? "s" : ""} sans établissement :</span>
                    <span className="truncate max-w-[360px]">{ov.unassigned.slice(0, 4).map(fullName).join(", ")}{ov.unassigned.length > 4 ? "…" : ""}</span>
                  </div>
                )}
              </div>
              {ov.sites.length === 0 ? (
                <div className="border-2 border-dashed border-[#DEE2E6] p-12 text-center" data-testid="sites-empty">
                  <Building2 className="h-12 w-12 mx-auto text-[#868E96] mb-4" />
                  <div className="text-lg font-semibold mb-2">Aucun établissement</div>
                  <div className="text-sm text-[#868E96]">Créez votre siège puis vos agences, puis affectez vos salariés à chacun d'eux.</div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sites-grid">
                  {ov.sites.map((s) => <SiteCard key={s.id} site={s} seasonal={ov.entreprise.seasonal_mode} onOpen={(site) => setOpenSite(site.id)} />)}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <SiteDetailSheet siteId={openSite} seasonalMode={ov?.entreprise?.seasonal_mode} unassigned={ov?.unassigned || []}
        onClose={() => setOpenSite(null)} onChanged={load} onEdit={(site) => setDialog({ open: true, site })} />
      <SiteInfoDialog open={dialog.open} site={dialog.site} onClose={() => setDialog({ open: false, site: null })} onSave={saveSite} />
    </div>
  );
}
